import { BundleCartItemWithProduct } from '@storefront/adapters/LineItemAdapter';
import {
  BundleItemOptionWithLowestPrice,
  BundleItemWithLowestPrice,
  BundleProductWithLowestPrice,
} from '@storefront/app/pdp-config/printer/product-configuration/product-configuration.utils';
import {
  BundleCartItem,
  BundleItem,
  BundleItemOption,
  BundleProduct,
  CartItemInput,
  GetCartItemsQuery,
  GetCustomerCartQuery,
} from '@storefront/data-commerce';

import {
  ProductData,
  ActiveProductState,
} from '../app/pdp-config/printer/state-management/pdp-config-state.utils';
import { IQueryProducts } from '../lib/query/queryParamsUtils';
import {
  CORE_OFFER_TYPE_LABEL_LIST,
  HpPlanOption,
  OptionsType,
  OptionsWithSteps,
  TotalStepType,
} from '../types/Product';

const optionHiddenValue = 0;

const productTypeLabels = {
  INSTANT_INK: 'instantInk',
  INSTANT_PAPER: 'instantPaper',
};

const getBundleProductFromCart = (
  cartData: GetCartItemsQuery
): BundleCartItem =>
  cartData.cart?.items.find(
    (item) => !!(item?.product as BundleProduct)?.items
  ) as BundleCartItem;

const getBundleProductFromCustomerCart = (customerCart: GetCustomerCartQuery) =>
  customerCart?.customerCart?.items.find(
    (item) => !!(item?.product as BundleProduct)?.items
  );

const getFirstCartItem = (cartData: GetCartItemsQuery): BundleCartItem =>
  cartData.cart?.items?.[0] as BundleCartItem;

const getProductItem = (product: ProductData): BundleProduct =>
  product.data.products.items[0];

const getPrimaryThumbnailURL = (product: BundleProduct) =>
  product?.items[0]?.options[0]?.product?.thumbnail?.url;

const getThumbnailAccessoriesURL = (product: BundleProduct) =>
  product?.items[0]?.options[0]?.product?.thumbnail_accessories?.url;

const getSimpleOptions = (
  product: BundleProductWithLowestPrice
): Array<BundleItemWithLowestPrice> =>
  // Get simple options and hid required options with size 1
  product.items.filter(
    (item: BundleItem) =>
      !item.json_option &&
      !(
        CORE_OFFER_TYPE_LABEL_LIST.includes(
          item.options[0].product.hp_product_type_label
        ) && item.required
      )
  );
const getAllSimpleOptions = (product: BundleProduct): Array<BundleItem> =>
  product.items.filter((item) => !item.json_option);

const getComplexOptions = (
  product: BundleProductWithLowestPrice
): Array<BundleItemWithLowestPrice> =>
  product?.items?.filter((item) =>
    item.options.find((option) =>
      CORE_OFFER_TYPE_LABEL_LIST.includes(option.product.hp_product_type_label)
    )
  );

const getCoreOffer = (item: BundleItem, product: BundleProduct) => {
  let option =
    item?.options?.find((option) => option.is_default) ?? item?.options?.[0];

  if (!option) return undefined;
  if (typeof window === 'undefined') {
    return undefined;
  }

  const currentUrl = window.location.href;
  const url = new URL(currentUrl);
  const skus = url.searchParams.get('skus')?.split('_');

  const fields = skus?.find((sku) => sku.includes(product.sku))?.split('-');

  if (fields?.length > 1) {
    const preSelectedCoreOffer = fields[1];
    // fallback is the default option if preselected core offer is not found
    option =
      item.options.find(
        (option) => option.product.sku === preSelectedCoreOffer
      ) ?? option;
  }
  return option;
};

const getProductTypeLabel = (product: BundleProduct): string =>
  product?.items?.[0]?.options?.[0]?.product?.hp_product_type_label;

const getVisibleNotRequiredSimpleOptions = (
  simpleOptions: Array<BundleItemWithLowestPrice>
): Array<BundleItemWithLowestPrice> =>
  simpleOptions.filter(
    (item) => item.hidden === optionHiddenValue && !item.required
  );

const getVisibleSimpleOptions = (
  simpleOptions: Array<BundleItemWithLowestPrice>,
  filterOptional = false
): Array<BundleItemWithLowestPrice> => {
  if (filterOptional) {
    return simpleOptions.filter(
      (item) => item.hidden === optionHiddenValue && item.required
    );
  }
  return simpleOptions.filter((item) => item.hidden === optionHiddenValue);
};

const getHiddenSimpleOptions = (product: BundleProduct): Array<BundleItem> => {
  const simpleOptions = getAllSimpleOptions(product);
  return simpleOptions.filter((item) => item.hidden === 1 && item.required);
};

const recursiveCallProducts = (
  coreOffer: OptionsType,
  levelCount: number
): number => {
  if (coreOffer?.products?.length) {
    levelCount++;
    recursiveCallProducts(coreOffer?.products[0], levelCount);
  }
  return levelCount;
};

const getTotalSteps = (
  product: BundleProductWithLowestPrice
): TotalStepType => {
  const simpleOptions = getSimpleOptions(product);
  const visibleSimpleOptions = getVisibleSimpleOptions(simpleOptions);
  const simpleTotalSteps = visibleSimpleOptions.length;
  let totalStep = {
    coreOfferTotalSteps: 0,
    simpleTotalSteps: simpleTotalSteps,
  };

  return totalStep;
};

const getOptionsWithSteps = ({
  product,
  filterOptional = false,
}: {
  product: BundleProductWithLowestPrice;
  filterOptional?: boolean;
}): OptionsWithSteps => {
  const simpleOptions = getSimpleOptions(product);
  const hiddenOptions = getHiddenSimpleOptions(product);
  const visibleSimpleOptions = getVisibleSimpleOptions(
    simpleOptions,
    filterOptional
  );

  let notRequiredOptions: Array<BundleItemWithLowestPrice> = [];
  if (filterOptional) {
    notRequiredOptions = getVisibleNotRequiredSimpleOptions(simpleOptions);
  }

  const coreOffer = getComplexOptions(product);
  const steps = getTotalSteps(product);

  return {
    visibleSimpleOptions: visibleSimpleOptions,
    hiddenOptions: hiddenOptions,
    coreOffer: coreOffer,
    notRequiredOptions,
    steps: steps,
  };
};

function getProductCartData(
  options: Record<string, string[]>
): CartItemInput | CartItemInput[] {
  let cartItems: CartItemInput[] = [];
  Object.keys(options).forEach((sku) => {
    if (options[sku].length > 0) {
      const cartItem: CartItemInput = {
        sku: sku,
        selected_options: options[sku],
        quantity: 1,
      };
      cartItems = cartItems.concat(cartItem);
    }
  });
  if (cartItems.length > 0) return cartItems;
}

const getRequiredSimpleOptions = (
  product: BundleProductWithLowestPrice
): string[] =>
  getSimpleOptions(product)
    .filter(
      (item) => item.required === true && item.hidden === optionHiddenValue
    )
    .map((item) => item.uid);

const getUIDFromRequiredSKUItems = (product: BundleProductWithLowestPrice) => {
  const requiredSKUItems: Record<string, BundleItemOptionWithLowestPrice> = {};

  getSimpleOptions(product)
    .filter(
      (item) => !item?.required && item?.hidden && item?.required_sku !== ''
    )
    .forEach((item) => {
      item.required_sku.split(',').forEach((sku) => {
        const uid = getUIDBySKU(product, sku);
        if (uid) {
          requiredSKUItems[uid] = item?.options?.[0];
        }
      });
    });
  return requiredSKUItems;
};

const getUIDBySKU = (product: BundleProduct, sku: string) =>
  product?.items
    .flatMap((item) => item?.options)
    .find((item) => item?.product?.sku === sku)?.uid;

const isOptionSelected = (
  products: BundleProduct[],
  required: string,
  queryParam: IQueryProducts
) => {
  let optionIsSelected = false;
  products.forEach((product) => {
    if (
      queryParam?.bundleOptions[product.sku] &&
      required in queryParam.bundleOptions[product.sku]
    ) {
      if (product.stock_status === 'IN_STOCK') optionIsSelected = true;
    }
  });

  return optionIsSelected;
};
const requiredComplexOptionsRecursive = (
  item: BundleItem,
  products?: OptionsType[]
) => {
  if (!products) return [];
  let complexOptions: string[] = [];
  products.forEach((complexOption) => {
    complexOptions = complexOptions.concat(
      getAttributeWithUid(item.uid, complexOption.attribute)
    );
    complexOptions = complexOptions.concat(
      requiredComplexOptionsRecursive(item, complexOption.products)
    );
  });
  return complexOptions;
};
const getRequiredComplexOptions = (
  product: BundleProductWithLowestPrice
): string[] => {
  const complexOptions = getComplexOptions(product)
    .filter(
      (item) => item.required === true && item.hidden === optionHiddenValue
    )
    .reduce((complexOptions, item) => {
      const options = JSON.parse(item.json_option) as OptionsType;
      return complexOptions.concat(
        requiredComplexOptionsRecursive(item, options.products)
      );
    }, []);

  return Array.from(new Set(complexOptions));
};

const getRequiredItems = (
  products: BundleProductWithLowestPrice[],
  activeProducts: ActiveProductState
): string[] =>
  products.reduce((requiredOptions, product) => {
    if (activeProducts.active[product.sku]) {
      return requiredOptions.concat([
        ...getRequiredSimpleOptions(product),
        ...getRequiredComplexOptions(product),
      ]);
    } else {
      return requiredOptions;
    }
  }, [] as string[]);

const getAttributeWithUid = (uid: string, attribute: string) =>
  `${uid}=${attribute}`;

const getSKUByUID = (product: BundleProduct, uid: string) =>
  product?.items

    .flatMap((item) => item?.options)

    .find((item) => item?.uid === uid)?.product?.sku;

const filterUnavailableInstantInkOptions = (
  options: BundleItemOptionWithLowestPrice[]
): BundleItemOptionWithLowestPrice[] =>
  options?.filter(
    (option) =>
      option.product?.hp_product_type_label !== productTypeLabels.INSTANT_INK ||
      option?.hp_plan_option === HpPlanOption.available
  );

const isBundleProductOutOfStock = (bundleProduct: BundleProduct): boolean => {
  const hiddenItems = bundleProduct.items.filter((item) => item.hidden === 1);
  const defaultHiddenItemsOptions = hiddenItems
    .flatMap((item) => item.options)
    .filter((option) => option.is_default);
  const isSomeHiddenOptionOutOfStock = defaultHiddenItemsOptions.some(
    (option) => option.product.stock_status === 'OUT_OF_STOCK'
  );
  return (
    bundleProduct.stock_status === 'OUT_OF_STOCK' ||
    isSomeHiddenOptionOutOfStock
  );
};

const getProductTypeLabelFromBundleCartItem = (
  item: BundleCartItemWithProduct
) => getProductTypeLabelFromItem(item?.product?.items[0]);

const getProductTypeLabelFromItem = (
  item: BundleItemWithLowestPrice | BundleItem
) => getProductTypeLabelFromOption(item?.options?.[0]);

const getProductTypeLabelFromOption = (
  option:
    | BundleItemOption
    | BundleItemOptionWithLowestPrice
    | BundleCartItemWithProduct
) => option?.product?.hp_product_type_label;

const getProductDataFromProduct = (product: BundleProduct) =>
  product?.items[0]?.options[0]?.product;

const getImagefromBundleProduct = (bundleProduct: BundleProduct) =>
  bundleProduct?.items?.[0]?.options?.[0]?.product?.image?.url;

const getShortDescription = (bundleProduct: BundleItemOptionWithLowestPrice) =>
  bundleProduct?.product?.short_description?.html;

const getProductSmallImageFromBundle = (
  bundleItem: BundleItemWithLowestPrice
) => bundleItem?.options[0]?.product?.small_image.url;

const getProductImageFromBundle = (bundleItem: BundleItemWithLowestPrice) =>
  bundleItem?.options[0]?.product?.image.url;

const getMonthlyPages = (
  bundleItemOption: BundleItemOption | BundleItemOptionWithLowestPrice
) => bundleItemOption?.product?.hp_pages_per_month;

const getContractDurationFromBundle = (
  bundleItemOption: BundleItemOptionWithLowestPrice
) => bundleItemOption?.product?.hp_contract_duration;

const getSelectedSkuFromCustomerCart = (customerCart: GetCustomerCartQuery) =>
  customerCart.customerCart?.items.find(
    (item) => !!(item?.product as BundleProduct)?.items
  )?.product?.sku;

const isTargetStateCart = (customerCart: GetCustomerCartQuery) =>
  customerCart.customerCart?.items.find(
    (item) => !!(item?.product as BundleProduct)?.items
  )?.product?.hp_bundle_lease_type === 'target';

const getTitleFromBundleItem = (bundleItem: BundleItem) =>
  bundleItem?.options?.[0]?.product?.hp_short_config_name;

const getNameFromBundleItemOption = (bundleItem: BundleItemOption) =>
  bundleItem?.product?.name;

const getSkuFromBundleCartItem = (bundleCartItem: BundleCartItemWithProduct) =>
  bundleCartItem?.bundle_options?.[0]?.values[0]?.sku;

const getLabelFromBundleCartItem = (
  bundleCartItem: BundleCartItemWithProduct
) => bundleCartItem?.product?.items[0]?.options[0]?.label;

export {
  filterUnavailableInstantInkOptions,
  getAllSimpleOptions,
  getAttributeWithUid,
  getFirstCartItem,
  getBundleProductFromCart,
  getBundleProductFromCustomerCart,
  getComplexOptions,
  getCoreOffer,
  getHiddenSimpleOptions,
  getOptionsWithSteps,
  getProductCartData,
  getProductItem,
  getPrimaryThumbnailURL,
  getThumbnailAccessoriesURL,
  getProductTypeLabel,
  getRequiredItems,
  getSKUByUID,
  getUIDBySKU,
  getUIDFromRequiredSKUItems,
  getSimpleOptions,
  getTotalSteps,
  getVisibleSimpleOptions,
  recursiveCallProducts,
  isOptionSelected,
  isBundleProductOutOfStock,
  getProductTypeLabelFromItem,
  getProductTypeLabelFromOption,
  getProductDataFromProduct,
  getImagefromBundleProduct,
  getShortDescription,
  getProductSmallImageFromBundle as getProductSmallImage,
  getProductImageFromBundle as getProductImage,
  getMonthlyPages,
  getContractDurationFromBundle,
  getSelectedSkuFromCustomerCart,
  getTitleFromBundleItem,
  getNameFromBundleItemOption,
  getSkuFromBundleCartItem,
  getLabelFromBundleCartItem,
  getProductTypeLabelFromBundleCartItem,
  isTargetStateCart,
};
